// ProfileUserScreen.jsx
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay'; // Import the new component
import defaultUserProfileImage from '../../assets/img/user-profile.jpg';
import PencilFillIcon from '../../components/icons/PencilFillIcon'; // Import the new icon
import { useLatinAmericaCountryOptions } from '../../hooks/useCountryOptions';

const ProfileUserScreen = () => {
    const { user, token, updateUserInContext } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        birthDate: '',
        country: '',
        newPassword: '',
        repeatPassword: '',
        profileImageFile: null,
        profileImagePreview: defaultUserProfileImage,
    });

    const [message, setMessage] = useState({ type: '', text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false); // New state for loading spinner

    const countryOptions = useLatinAmericaCountryOptions();

    useEffect(() => {
        if (user) {
            // console.log('[ProfileUserScreen] useEffect - user from context:', JSON.parse(JSON.stringify(user)));
            let determinedProfileImage = defaultUserProfileImage;
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

            if (user.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/files/serve/${user.avatar_file_id}`;
            } else if (user.image && typeof user.image === 'string' && user.image.startsWith('http')) { // Check if user.image is a valid URL
                determinedProfileImage = user.image;
            }

            setFormData(prev => ({
                ...prev,
                name: user.name || '',
                email: user.email || '',
                birthDate: user.birth_date ? user.birth_date.split('T')[0] : '',
                country: user.country || '',
                profileImagePreview: determinedProfileImage,
                // Reset password fields and file input when user context changes,
                // unless you want to persist them across user sessions (unlikely for this form)
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
            }));
        } else {
            setFormData({
                name: '',
                email: '',
                birthDate: '',
                country: '',
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
                profileImagePreview: defaultUserProfileImage,
            });
        }
    }, [user]); // Rerun when user object from context changes

    useEffect(() => {
        const currentPreview = formData.profileImagePreview;
        if (currentPreview && currentPreview.startsWith('blob:')) {
            return () => {
                URL.revokeObjectURL(currentPreview);
            };
        }
    }, [formData.profileImagePreview]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({
                ...prev,
                profileImageFile: file,
                profileImagePreview: URL.createObjectURL(file)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setIsSubmitting(true); // Start loading

        if (formData.newPassword && formData.newPassword !== formData.repeatPassword) {
            setMessage({ type: 'danger', text: 'Las nuevas contraseñas no coinciden.' });
            setIsSubmitting(false); // Stop loading
            return;
        }

        if (!user || !user.id) {
            setMessage({ type: 'danger', text: 'Error: No se pudo identificar al usuario.' });
            setIsSubmitting(false); // Stop loading
            return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        try {
            let newUploadedAvatarFileId = null;

            if (formData.profileImageFile) {
                console.log('Uploading new profile image...');
                const imageFormData = new FormData();
                imageFormData.append('avatar', formData.profileImageFile);

                const imageUploadResponse = await fetch(`${API_BASE_URL}/api/files/upload/avatar`, {
                    method: 'POST',
                    body: imageFormData,
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                });

                if (!imageUploadResponse.ok) {
                    if (imageUploadResponse.status === 401) throw new Error('No autorizado para subir imagen. Por favor, inicie sesión de nuevo.');
                    const errorData = await imageUploadResponse.json().catch(() => ({ message: 'Error al subir la imagen. Respuesta no válida.' }));
                    throw new Error(errorData.message || `Error al subir la imagen: ${imageUploadResponse.statusText}`);
                }
                const uploadResult = await imageUploadResponse.json();
                if (uploadResult.file_id) {
                    newUploadedAvatarFileId = uploadResult.file_id;
                } else {
                    throw new Error('No se recibió file_id después de subir la imagen.');
                }
            }

            const userUpdatePayload = {
                username: formData.name,
                email: user.email, // Email is from context, not form, as it's disabled
                initial_form_json: user.initial_form_json || null,
                birth_date: formData.birthDate,
                country: formData.country,
            };

            // Add password to payload if newPassword field is filled
            if (formData.newPassword) {
                userUpdatePayload.password = formData.newPassword; // Or the field name your API expects for a new password
            }

            if (newUploadedAvatarFileId) {
                userUpdatePayload.avatar_file_id = newUploadedAvatarFileId;
            } else if (user.avatar_file_id && !formData.profileImageFile) {
                userUpdatePayload.avatar_file_id = user.avatar_file_id;
            } else if (!newUploadedAvatarFileId && !user.avatar_file_id) {
                // If no new image and no existing avatar_file_id, explicitly set to null
                // if your API expects this to remove an avatar. Otherwise, omit.
                userUpdatePayload.avatar_file_id = null;
            }


            console.log('Updating user profile with payload:', userUpdatePayload);
            const userUpdateResponse = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(userUpdatePayload),
            });

            if (!userUpdateResponse.ok) {
                if (userUpdateResponse.status === 401) throw new Error('No autorizado para actualizar el perfil. Por favor, inicie sesión de nuevo.');
                const errorData = await userUpdateResponse.json().catch(() => ({ message: 'Error al actualizar el perfil. Respuesta no válida.' }));
                throw new Error(errorData.message || `Error al actualizar el perfil: ${userUpdateResponse.statusText}`);
            }

            const updatedUserFromServer = await userUpdateResponse.json();
            console.log('[ProfileUserScreen] RAW Server PUT response (updatedUserFromServer):', JSON.parse(JSON.stringify(updatedUserFromServer)));

            // Construct the object to be stored in AuthContext and localStorage.
            // Prioritize the data that was successfully sent in the PUT payload for the fields that were updated.
            const finalUserDataForContext = {
                ...user, // Start with current user from context (preserves id, email, initial_form_json, existing avatar_file_id if not changed by upload)
                username: userUpdatePayload.username, // Use the username that was sent and confirmed by successful PUT
                birth_date: userUpdatePayload.birth_date, // Use the birth_date that was sent
                country: userUpdatePayload.country, // Use the country that was sent
                // If a new avatar was uploaded, newUploadedAvatarFileId will be set from the avatar upload response.
                // This will overwrite user.avatar_file_id from the spread above if newUploadedAvatarFileId is not null.
                // If no new avatar was uploaded, userUpdatePayload.avatar_file_id (which would be the existing one or null) is used.
                avatar_file_id: userUpdatePayload.avatar_file_id,
            };

            updateUserInContext(finalUserDataForContext);

            // For immediate UI feedback, set formData based on the data we've decided is the "truth" for the context.
            let determinedProfileImage = defaultUserProfileImage;
            if (finalUserDataForContext.avatar_file_id) { // Use avatar_file_id from our "truth" object
                determinedProfileImage = `${API_BASE_URL}/files/serve/${finalUserDataForContext.avatar_file_id}`;
            }

            setFormData({
                name: finalUserDataForContext.username || '', // Form field 'name' maps to 'username'
                email: finalUserDataForContext.email || '', // Email is from context, not directly updatable in this form
                birthDate: finalUserDataForContext.birth_date ? finalUserDataForContext.birth_date.split('T')[0] : '',
                country: finalUserDataForContext.country || '',
                profileImagePreview: determinedProfileImage,
                profileImageFile: null, // Clear uploaded file from local state
                newPassword: '', // Clear password fields
                repeatPassword: '',
            });


            if (formData.newPassword) { // Check original formData for newPassword intent
                console.log('Changing password (simulated - API call not implemented here)...');
                // Implement password change API call if needed
            }

            setMessage({ type: 'success', text: 'Perfil actualizado con éxito.' });

        } catch (error) {
            console.error("Error submitting profile:", error);
            setMessage({ type: 'danger', text: error.message || 'Error al guardar los cambios.' });
        } finally {
            setIsSubmitting(false); // Stop loading regardless of outcome
        }
    };

    return (
        <DashboardLayout activeMenuItem="Inicio" isScrollable={true}>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-4">Mi Perfil</h1>
            </div>
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <FormSpinnerOverlay isLoading={isSubmitting} />
                        <div className="card-body">
                            {message.text && <AlertMessage type={message.type} message={message.text} />}
                            <form id="profileForm" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-3 text-center mb-3 mb-md-0">
                                        <div className="position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={formData.profileImagePreview || defaultUserProfileImage}
                                                id="profileImagePreview"
                                                alt="Avatar"
                                                className="img-fluid rounded-circle shadow-sm"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <label htmlFor="profileImageInput" className="btn btn-sm btn-checkin position-absolute p-2 rounded-circle" style={{ bottom: '5px', right: '5px', cursor: 'pointer', lineHeight: 1, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} title="Editar imagen">
                                                <PencilFillIcon />
                                                <span className="visually-hidden">Editar imagen</span>
                                            </label>
                                            <input type="file" id="profileImageInput" name="profileImage" className="d-none" accept="image/*" onChange={handleImageChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-3 mb-md-0 text-start">
                                        <TextInput
                                            label="Nombre"
                                            id="profileName"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <TextInput
                                            label="Email"
                                            id="profileEmail"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            disabled
                                        />
                                        <TextInput
                                            label="Fecha de nacimiento"
                                            id="birthDate"
                                            name="birthDate"
                                            type="date"
                                            value={formData.birthDate}
                                            onChange={handleChange}
                                            required
                                            iconClass="bi bi-calendar3"
                                            ariaLabel="Fecha de nacimiento"
                                        />
                                    </div>
                                    <div className="col-md-4 text-start">
                                        <div className="mb-3">
                                            <SelectInput
                                                label="País de origen"
                                                id="country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                options={countryOptions}
                                                defaultOptionText="Selecciona tu país..."
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput
                                                label="Nueva contraseña"
                                                id="newPassword"
                                                name="newPassword"
                                                value={formData.newPassword}
                                                onChange={handleChange}
                                                autoComplete="new-password"
                                                ariaLabel="Campo para nueva contraseña"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput
                                                label="Repita la nueva contraseña"
                                                id="repeatPassword"
                                                name="repeatPassword"
                                                value={formData.repeatPassword}
                                                onChange={handleChange}
                                                autoComplete="new-password"
                                                ariaLabel="Campo para repetir nueva contraseña"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <button type="submit" className="btn btn-checkin px-4">Guardar Cambios</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ProfileUserScreen;
